<script>
import { onMount, onDestroy, untrack } from "svelte";
import { Stage, Layer, Rect } from "svelte-konva";
import throttle from "just-throttle";
import ccEvent from "@cclib/classes/event.js";

import { installTimelineGlobals, uninstallTimelineGlobals } from "@app/lib/timeline/globalShim.js";
import {
    viewportState,
    setContainerWidth,
    setDuration,
    resetViewport,
    zoomIn,
    zoomOut,
    panBy,
    setScrollX,
    centerOnTime
} from "@app/lib/timeline/viewport.svelte.js";
import { applyRegionClick, dispatchScrollToEvent } from "@app/lib/timeline/selection.js";
import {
    classifyDrawRange,
    clampDrawRange,
    drawFrameGapSec
} from "@app/lib/timeline/drawCreate.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { editorState } from "@app/store/editorStore.svelte.js";
import { playerState } from "@app/store/playerStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";

import TimelineRuler from "./TimelineRuler.svelte";
import TimelineWaveform from "./TimelineWaveform.svelte";
import TimelineTrack from "./TimelineTrack.svelte";
import TimelineMarkers from "./TimelineMarkers.svelte";
import TimelineFilmstrip from "./TimelineFilmstrip.svelte";
import TimelinePlayhead from "./TimelinePlayhead.svelte";
import TimelineMinimap from "./TimelineMinimap.svelte";
import TimelineToolbar from "./TimelineToolbar.svelte";
import TimelineTrackLabels from "./TimelineTrackLabels.svelte";
import { timelineTracksState, addTrack } from "@app/store/timelineTracksStore.svelte.js";
import { v4 as uuidv4 } from "uuid";
import convertToPlainTextCustom from "@cclib/functions/quill/convertToPlainTextCustom.js";
import convertToHtml from "@cclib/functions/quill/convertToHtml.js";
import removeEvent from "@cclib/functions/eventGroups/removeEvent.js";
import { canGenerateFilmstrip } from "@app/lib/filmstrip.js";
import { environment } from "@app/store/envStore.svelte.js";
import { resetPeaksState } from "@app/lib/timeline/peaksState.svelte.js";

/**
 * @typedef {Object} Props
 * @property {number} [timelineHeight]
 */

/** @type {Props} */
let { timelineHeight = 150 } = $props();

const RULER_HEIGHT = 22;
const RULER_PAN_CLICK_THRESHOLD_PX = 4;
const FILMSTRIP_TRACK_HEIGHT = 58;
const MINIMAP_HEIGHT = 44;
const LABELS_GUTTER_WIDTH = 140;
const MIN_TRACK_HEIGHT = 36;

let wrapperEl;
let containerEl;
let resizeObserver;

let showMarkers = $state(true);

let filmstripTrackVisible = $derived(
    Boolean(environment.electron || environment.desktop) &&
    Boolean(editorState.showFilmstrip) &&
    canGenerateFilmstrip(projectState)
);

let stageHeight = $derived(Math.max(48, timelineHeight - MINIMAP_HEIGHT));
let stageWidth = $derived(Math.max(0, viewportState.containerWidth));
// Shared vertical span for ruler-through-body overlay elements (markers, playhead).
const contentY = RULER_HEIGHT;
let contentHeight = $derived(stageHeight - RULER_HEIGHT);
let filmstripY = $derived(RULER_HEIGHT);
let bodyY = $derived(RULER_HEIGHT + (filmstripTrackVisible ? FILMSTRIP_TRACK_HEIGHT : 0));
let bodyHeight = $derived(Math.max(0, stageHeight - bodyY));

// Compute the ordered list of tracks (primary + additional) plus the y/height
// each one occupies inside the body area.
let trackLayout = $derived.by(() => {
    const primaryIdx = projectState.selected;
    const trackList = [];
    if (eventGroupState[primaryIdx]) {
        trackList.push({ index: primaryIdx, primary: true });
    }
    for (let i = 0; i < timelineTracksState.additional.length; i += 1) {
        const idx = timelineTracksState.additional[i];
        if (idx === primaryIdx) continue;
        if (!eventGroupState[idx]) continue;
        trackList.push({ index: idx, primary: false });
    }
    if (trackList.length === 0) return { rows: [], rowHeight: bodyHeight };
    const rowHeight = Math.max(MIN_TRACK_HEIGHT, Math.floor(bodyHeight / trackList.length));
    return {
        rows: trackList.map((t, i) => ({ ...t, y: bodyY + i * rowHeight, height: rowHeight })),
        rowHeight
    };
});

function measureContainer() {
    if (!containerEl) return;
    setContainerWidth(containerEl.clientWidth);
}

// Throttle measurements to one per animation frame; ResizeObserver can fire
// rapidly during splitpane drags.
const throttledMeasure = throttle(measureContainer, 16, { leading: false, trailing: true });

/**
 * Convert a wheel event into either a zoom-around-cursor or a horizontal pan,
 * depending on modifier keys. preventDefault stops the host page scroll.
 */
function handleWheel(event) {
    if (!viewportState.containerWidth) return;
    event.preventDefault();

    const rect = containerEl.getBoundingClientRect();
    const stageX = event.clientX - rect.left;

    // Some mice expose deltaX for horizontal scroll wheels; fall back to deltaY
    // when the user holds Shift on a regular vertical wheel (matches the spec).
    if (event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        const dx = event.deltaX || event.deltaY;
        panBy(dx);
        return;
    }

    if (event.deltaY < 0) {
        zoomIn(stageX);
    } else if (event.deltaY > 0) {
        zoomOut(stageX);
    }
}

onMount(() => {
    installTimelineGlobals();
    migrateCompareToAdditional();
    measureContainer();
    if (typeof ResizeObserver !== "undefined" && containerEl) {
        resizeObserver = new ResizeObserver(() => throttledMeasure());
        resizeObserver.observe(containerEl);
    }
});

onDestroy(() => {
    resizeObserver?.disconnect();
    endRulerPanDrag();
    uninstallTimelineGlobals();
    resetViewport();
    resetPeaksState();
});

// Track media duration so the viewport can clamp scroll to the legal range.
$effect(() => {
    setDuration(playerState.duration || 0);
});

// TimelinePlayhead renders and scrubs only; this parent owns viewport follow.
// Track both time and playing so playback starts with the playhead moving until
// it reaches center, then the viewport scrolls to keep it centered. While
// paused, only recenter external seeks or scrubs that put the playhead
// offscreen, preserving manual pan/zoom. Viewport reads stay `untrack`ed so
// user panning or zooming does not by itself re-run this effect.
$effect(() => {
    const time = playerState.time;
    const playing = playerState.playing;
    untrack(() => {
        const pxPerSec = viewportState.pxPerSec;
        const width = viewportState.containerWidth;
        if (!pxPerSec || !width) return;
        const playheadX = time * pxPerSec - viewportState.scrollX;
        const playheadOffscreen = playheadX < 0 || playheadX > width;
        if (playheadOffscreen || (playing && playheadX >= width / 2)) {
            centerOnTime(time);
        }
    });
});

/**
 * Selection handler invoked by each track when the user clicks a region in
 * the primary track. Mirrors the legacy WaveSurfer timeline behaviour:
 *  - mutate eventGroupState[primary].selected per modifier keys
 *  - blur the active editor so keyboard shortcuts route to the timeline
 *  - dispatch scrollToVirtualListItem so the editor list scrolls in sync
 */
function handlePrimaryRegionClick(eventIndex, evt) {
    const groupIdx = projectState.selected;
    const group = eventGroupState[groupIdx];
    if (!group) return;

    const next = applyRegionClick({
        eventIndex,
        modifiers: evt || {},
        currentSelected: group.selected
    });
    group.selected = next;
    editorState.editing = false;
    if (typeof document !== "undefined") {
        document.activeElement?.blur?.();
    }
    if (typeof window !== "undefined") window.focus();
    dispatchScrollToEvent(eventIndex);
}

/**
 * Double-click a region: seek the player to the region's start time.
 */
function handlePrimaryRegionDoubleClick(eventIndex) {
    const groupIdx = projectState.selected;
    const event = eventGroupState[groupIdx]?.events?.[eventIndex];
    if (!event || !Number.isFinite(event.start)) return;
    if (window.player) window.player.currentTime = event.start;
}

/**
 * After a drag or resize commits to the store, snapshot the eventGroupState
 * into history so the change can be undone. Matches the legacy timeline's
 * action name "timecode updated" so existing undo/redo UI remains coherent.
 */
function commitDragHistory() {
    try {
        insertHistoryAction({
            name: "timecode updated",
            eventGroup: projectState.selected,
            snapshots: [
                { store: "eventGroupState", value: JSON.stringify(eventGroupState) }
            ]
        });
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[timeline] commitDragHistory failed:", err?.message);
    }
}

// ===== Draw-to-create =====
// When `editorState.drawEvents` is true and the user mouse-downs on an empty
// track row, dragging creates a new event in that row's event group.

let drawState = $state({
    active: false,
    groupIndex: null,
    startTime: 0,
    currentTime: 0,
    rowY: 0,
    rowHeight: 0
});

function onWindowDrawMove(ev) {
    if (!drawState.active) return;
    const rect = containerEl?.getBoundingClientRect();
    if (!rect) return;
    if (!viewportState.pxPerSec) return;
    const x = ev.clientX - rect.left;
    drawState.currentTime = (x + viewportState.scrollX) / viewportState.pxPerSec;
}

function onWindowDrawUp() {
    if (!drawState.active) return;
    window.removeEventListener("mousemove", onWindowDrawMove);
    window.removeEventListener("mouseup", onWindowDrawUp);
    const start = Math.min(drawState.startTime, drawState.currentTime);
    const end = Math.max(drawState.startTime, drawState.currentTime);
    const groupIndex = drawState.groupIndex;
    drawState.active = false;
    drawState.groupIndex = null;
    commitDrawnEvent(groupIndex, start, end);
}

function handleBackgroundPointerDown(groupIndex, rowY, rowHeight, timeSec, evt) {
    // Only respond to primary mouse button.
    if (evt && evt.button !== undefined && evt.button !== 0) return;
    // Always seek the player when clicking an empty track area, regardless of
    // whether draw-to-create mode is active. A subsequent draw drag still gets
    // initiated below when `editorState.drawEvents` is on.
    if (window.player && Number.isFinite(timeSec) && timeSec >= 0) {
        window.player.currentTime = timeSec;
    }
    if (!editorState.drawEvents) return;
    if (!eventGroupState[groupIndex]) return;
    window.removeEventListener("mousemove", onWindowDrawMove);
    window.removeEventListener("mouseup", onWindowDrawUp);
    drawState.active = true;
    drawState.groupIndex = groupIndex;
    drawState.startTime = timeSec;
    drawState.currentTime = timeSec;
    drawState.rowY = rowY;
    drawState.rowHeight = rowHeight;
    window.addEventListener("mousemove", onWindowDrawMove);
    window.addEventListener("mouseup", onWindowDrawUp);
}

// ===== Ruler drag-to-pan =====
/** @type {{ pointerId: number | null, startClientX: number, startScrollX: number, panned: boolean } | null} */
let rulerPanDrag = null;

function endRulerPanDrag() {
    if (!rulerPanDrag) return;
    rulerPanDrag = null;
    if (containerEl) containerEl.style.cursor = "";
    window.removeEventListener("pointermove", onWindowRulerPanMove, true);
    window.removeEventListener("pointerup", onWindowRulerPanUp, true);
    window.removeEventListener("pointercancel", onWindowRulerPanCancel, true);
}

function isRulerPanPointer(evt) {
    return !rulerPanDrag || rulerPanDrag.pointerId === null || evt.pointerId === rulerPanDrag.pointerId;
}

function onWindowRulerPanMove(evt) {
    if (!rulerPanDrag || !isRulerPanPointer(evt)) return;
    evt.preventDefault?.();
    const dx = evt.clientX - rulerPanDrag.startClientX;
    if (!rulerPanDrag.panned && Math.abs(dx) > RULER_PAN_CLICK_THRESHOLD_PX) {
        rulerPanDrag.panned = true;
    }
    if (!rulerPanDrag.panned) return;
    // Dragging right pulls earlier timeline content into view (decrease scrollX).
    setScrollX(rulerPanDrag.startScrollX - dx);
}

function onWindowRulerPanUp(evt) {
    if (!rulerPanDrag || !isRulerPanPointer(evt)) return;
    evt.preventDefault?.();
    const wasPan = rulerPanDrag.panned;
    const rect = containerEl?.getBoundingClientRect();
    if (!wasPan && rect && viewportState.pxPerSec) {
        const stageX = evt.clientX - rect.left;
        const time = (stageX + viewportState.scrollX) / viewportState.pxPerSec;
        if (window.player && Number.isFinite(time) && time >= 0) {
            window.player.currentTime = time;
        }
    }
    endRulerPanDrag();
}

function onWindowRulerPanCancel(evt) {
    if (!rulerPanDrag || !isRulerPanPointer(evt)) return;
    endRulerPanDrag();
}

function handleRulerPointerEnter() {
    if (!rulerPanDrag && containerEl) containerEl.style.cursor = "grab";
}

function handleRulerPointerLeave() {
    if (!rulerPanDrag && containerEl) containerEl.style.cursor = "";
}

function handleRulerPointerDown(e) {
    if (e?.evt?.button !== undefined && e.evt.button !== 0) return;
    endRulerPanDrag();
    rulerPanDrag = {
        pointerId: e.evt?.pointerId ?? null,
        startClientX: e.evt.clientX,
        startScrollX: viewportState.scrollX,
        panned: false
    };
    if (containerEl) containerEl.style.cursor = "grabbing";
    e.evt?.preventDefault?.();
    e.evt?.stopPropagation?.();
    e.cancelBubble = true;
    window.addEventListener("pointermove", onWindowRulerPanMove, true);
    window.addEventListener("pointerup", onWindowRulerPanUp, true);
    window.addEventListener("pointercancel", onWindowRulerPanCancel, true);
}

function getTrackAtStageY(stageY) {
    return trackLayout.rows.find((track) => stageY >= track.y && stageY < track.y + track.height);
}

/**
 * Stage-level pointerdown handler. Fires for clicks on parts of the timeline
 * not covered by an interactive (listening) node -- the waveform and keyframe
 * areas are on a `listening:false` layer so their clicks bubble up to the
 * Stage. The ruler has its own pan/seek handler; regions, handles, and the
 * playhead intercept their own events.
 */
function handleStagePointerDown(e) {
    if (e?.evt?.button !== undefined && e.evt.button !== 0) return;
    const stage = e?.target?.getStage?.();
    if (!stage || e.target !== stage) return;
    const pointer = stage.getPointerPosition?.();
    if (!pointer || !viewportState.pxPerSec) return;
    const time = (pointer.x + viewportState.scrollX) / viewportState.pxPerSec;
    const track = getTrackAtStageY(pointer.y);
    if (track) {
        handleBackgroundPointerDown(track.index, track.y, track.height, time, e.evt);
        return;
    }
    if (window.player && Number.isFinite(time) && time >= 0) {
        window.player.currentTime = time;
    }
}

function commitDrawnEvent(groupIndex, startTimeSec, endTimeSec) {
    const group = eventGroupState[groupIndex];
    if (!group) return;
    const events = group.events;
    const classification = classifyDrawRange(events, startTimeSec, endTimeSec);
    if (classification.overlap) return;

    const clamped = clampDrawRange({
        startTime: startTimeSec,
        endTime: endTimeSec,
        prev: classification.prev,
        next: classification.next,
        frameGapSec: drawFrameGapSec(editorState, projectState)
    });
    if (!clamped) return;

    let newEvent;
    if (group.type === "audio description" && events.length > 0) {
        const last = events[events.length - 1];
        newEvent = {
            ...new ccEvent({
                start: clamped.startTime,
                end: clamped.endTime,
                text: "",
                voice: last.voice,
                speakers: last.speakers,
                alignment: last.alignment,
                rate: last.rate,
                speakingStyle: last.speakingStyle,
                voiceStability: last.voiceStability,
                voiceSimilarity: last.voiceSimilarity
            })
        };
    } else {
        newEvent = {
            ...new ccEvent({ start: clamped.startTime, end: clamped.endTime, text: "" })
        };
    }

    const insertIndex = events.findIndex((ev) => ev.start > newEvent.start);
    if (insertIndex === -1) {
        events.push(newEvent);
        group.selected = [events.length - 1];
    } else {
        events.splice(insertIndex, 0, newEvent);
        group.selected = [insertIndex];
    }

    if (window.player) window.player.currentTime = newEvent.start;
    if (groupIndex === projectState.selected) {
        dispatchScrollToEvent(group.selected[0]);
    }
    try {
        insertHistoryAction({
            name: "insert event",
            eventGroup: groupIndex,
            snapshots: [
                { store: "eventGroupState", value: JSON.stringify(eventGroupState) }
            ]
        });
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[timeline] insert history failed:", err?.message);
    }
}

let drawPreviewBox = $derived.by(() => {
    if (!drawState.active) return null;
    const pxPerSec = viewportState.pxPerSec;
    if (!pxPerSec) return null;
    const a = Math.min(drawState.startTime, drawState.currentTime);
    const b = Math.max(drawState.startTime, drawState.currentTime);
    return {
        x: Math.round(a * pxPerSec - viewportState.scrollX),
        y: Math.round(drawState.rowY + 2),
        width: Math.max(1, Math.round((b - a) * pxPerSec)),
        height: Math.max(1, Math.round(drawState.rowHeight - 4))
    };
});

// ===== Split / Merge =====
/**
 * Split the event currently under the playhead at the playhead time. Mirrors
 * the legacy WaveSurfer timeline implementation so undo/redo records the same
 * "split event" action name.
 */
function splitAtPlayhead() {
    const groupIdx = projectState.selected;
    const group = eventGroupState[groupIdx];
    if (!group) return;
    if (!window.player) return;
    const sec = window.player.currentTime;
    const idx = group.events.findIndex((ev) => ev.start <= sec && ev.end >= sec);
    if (idx === -1) return;

    const copy = JSON.parse(JSON.stringify(group.events[idx]));
    copy.id = uuidv4();
    copy.start = sec;
    group.events[idx].end = sec;
    group.events.splice(idx + 1, 0, copy);

    try {
        insertHistoryAction({
            name: "split event",
            eventGroup: groupIdx,
            snapshots: [
                { store: "eventGroupState", value: JSON.stringify(eventGroupState) }
            ]
        });
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[timeline] split history failed:", err?.message);
    }
}

/**
 * Merge all currently selected events into a single event in the active group.
 * No-op when fewer than 2 events are selected.
 */
function mergeSelectedEvents() {
    const groupIdx = projectState.selected;
    const group = eventGroupState[groupIdx];
    if (!group) return;
    const selected = Array.isArray(group.selected) ? [...group.selected] : [];
    if (selected.length < 2) return;

    // Reverse sort by index (so splices don't invalidate later indexes).
    selected.sort((a, b) => b - a);

    let text = "";
    let originalText = "";
    let speakers = [];
    const lastEnd = group.events[selected[0]].end;

    selected.forEach((eventIndex, selectedIndex) => {
        const ev = group.events[eventIndex];
        text = convertToPlainTextCustom(ev.text) + "\n" + convertToPlainTextCustom(text);
        originalText = convertToPlainTextCustom(ev.original) + "\n" + convertToPlainTextCustom(originalText);
        if (Array.isArray(ev.speakers) && ev.speakers.length > 0) {
            speakers = [...speakers, ...ev.speakers];
        }
        if (selectedIndex < selected.length - 1) {
            eventGroupState[groupIdx] = removeEvent(eventGroupState[groupIdx], eventIndex);
        }
    });

    // De-duplicate speakers by id.
    const uniqueSpeakers = [];
    const seenIds = new Set();
    speakers.forEach((sp) => {
        if (sp && sp.id && !seenIds.has(sp.id)) {
            seenIds.add(sp.id);
            uniqueSpeakers.push(sp);
        }
    });

    const targetIdx = selected[selected.length - 1];
    const target = eventGroupState[groupIdx].events[targetIdx];
    if (target) {
        target.text = convertToHtml(text);
        target.original = convertToHtml(originalText);
        target.end = lastEnd || target.end;
        target.speakers = uniqueSpeakers;
        target.rendered = false;
    }
    eventGroupState[groupIdx].selected = [];

    try {
        insertHistoryAction({
            name: "merge events",
            eventGroup: groupIdx,
            snapshots: [
                { store: "eventGroupState", value: JSON.stringify(eventGroupState) }
            ]
        });
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[timeline] merge history failed:", err?.message);
    }
}

function toggleMarkers() {
    showMarkers = !showMarkers;
}

let selectedCount = $derived(eventGroupState[projectState.selected]?.selected?.length || 0);

/**
 * One-shot migration from the legacy `projectState.compare` (a single
 * additional event-group index, or `false`) into the new
 * `timelineTracksState.additional` model. Idempotent: if compare is already
 * `false` we do nothing; otherwise we move the value over and clear compare.
 */
function migrateCompareToAdditional() {
    const compare = projectState.compare;
    if (compare === false || compare === null || compare === undefined) return;
    addTrack(compare, projectState.selected);
    projectState.compare = false;
}
</script>

<svelte:window onresize={throttledMeasure} />

<div
    bind:this={wrapperEl}
    id="KonvaTimeline"
    role="region"
    aria-label="Timeline"
    style="height: {timelineHeight}px;">
    <TimelineMinimap height={MINIMAP_HEIGHT} />
    <div class="timeline-body">
        <div class="timeline-gutter" style="width: {LABELS_GUTTER_WIDTH}px;">
            <TimelineTrackLabels
                rowHeight={trackLayout.rowHeight}
                topPadding={bodyY} />
        </div>
        <div
            class="timeline-stage-area"
            bind:this={containerEl}
            onwheel={handleWheel}>
            {#if stageWidth > 0}
                <Stage
                    width={stageWidth}
                    height={stageHeight}
                    divWrapperProps={{ class: "konva-stage-wrapper" }}
                    onpointerdown={handleStagePointerDown}>
                    <!--
                        Background: static / low-frequency, non-listening content.
                        Markers render on the overlay layer (above tracks) so
                        they stay visible over event regions and the ruler band.
                        Paint order: waveform -> ruler (top).
                    -->
                    <Layer listening={false}>
                        <TimelineWaveform y={bodyY} height={bodyHeight} />
                        <TimelineRuler height={RULER_HEIGHT} bodyHeight={contentHeight} width={stageWidth} />
                    </Layer>

                    <!--
                        Film strip: interactive (listening) row of frame
                        thumbnails for the selected event. Clicking a thumbnail
                        seeks the player, so it needs its own listening layer.
                    -->
                    {#if filmstripTrackVisible}
                        <Layer>
                            <TimelineFilmstrip
                                y={filmstripY}
                                width={stageWidth}
                                height={FILMSTRIP_TRACK_HEIGHT} />
                        </Layer>
                    {/if}

                    <!-- Tracks: primary + additional. Each gets its own row. -->
                    <Layer>
                        {#each trackLayout.rows as track (track.index)}
                            <TimelineTrack
                                groupIndex={track.index}
                                isPrimary={track.primary}
                                y={track.y}
                                height={track.height}
                                onRegionClick={track.primary ? handlePrimaryRegionClick : undefined}
                                onRegionDoubleClick={track.primary ? handlePrimaryRegionDoubleClick : undefined}
                                onDragEndCommit={track.primary ? commitDragHistory : undefined}
                                onBackgroundPointerDown={(time, evt) => handleBackgroundPointerDown(
                                    track.index,
                                    track.y,
                                    track.height,
                                    time,
                                    evt
                                )} />
                        {/each}
                    </Layer>

                    <!--
                        Overlay: markers and playhead span ruler + body above
                        tracks. TimelinePlayhead renders/scrubs the playhead;
                        this component controls auto-follow behavior.
                        Draw order: ruler pan hit-area -> draw preview -> markers
                        -> playhead (top). The playhead head stays listening so
                        the user can scrub; the ruler rect handles drag-to-pan.
                    -->
                    <Layer>
                        <Rect
                            x={0}
                            y={0}
                            width={stageWidth}
                            height={RULER_HEIGHT}
                            fill="transparent"
                            listening={true}
                            staticConfig={true}
                            onpointerenter={handleRulerPointerEnter}
                            onpointerleave={handleRulerPointerLeave}
                            onpointerdown={handleRulerPointerDown} />
                        {#if drawPreviewBox}
                            <Rect
                                x={drawPreviewBox.x}
                                y={drawPreviewBox.y}
                                width={drawPreviewBox.width}
                                height={drawPreviewBox.height}
                                fill="rgba(13, 110, 253, 0.35)"
                                stroke="#0d6efd"
                                strokeWidth={1}
                                cornerRadius={3}
                                listening={false} />
                        {/if}
                        <TimelineMarkers y={contentY} height={contentHeight} show={showMarkers} />
                        <TimelinePlayhead y={contentY} height={contentHeight} />
                    </Layer>
                </Stage>
            {/if}

            <TimelineToolbar
                showMarkers={showMarkers}
                selectedCount={selectedCount}
                onToggleMarkers={toggleMarkers}
                onSplit={splitAtPlayhead}
                onMerge={mergeSelectedEvents} />
        </div>
    </div>
</div>

<style>
#KonvaTimeline {
    background: var(--bs-body-bg);
    color: var(--bs-body-color);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    user-select: none;
}

.timeline-body {
    display: flex;
    flex: 1;
    min-height: 0;
    overflow: hidden;
}

.timeline-gutter {
    flex: 0 0 auto;
    overflow: hidden;
}

.timeline-stage-area {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    position: relative;
}

/* Stage wrapper div created by svelte-konva. */
.timeline-stage-area :global(.konva-stage-wrapper) {
    height: 100%;
    width: 100%;
}
</style>
